/**
 * API Service for VegaDigital Solutions
 * Handles all communication with the backend API
 */

import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../config/apiConfig';

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
}

export interface ContactFormData {
  name: string;
  email: string;
  company: string;
  phone?: string;
  projectType?: string;
  budget?: string;
  message: string;
}

export interface ContactFormResponse {
  success: boolean;
  message: string;
  request_id: string;
}

export interface ValidationError {
  field: string;
  message: string;
}

class ApiService {
  private defaultHeaders: HeadersInit;

  constructor() {
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }
  
  private get baseUrl(): string {
    return getApiBaseUrl();
  }

  /**
   * Generic HTTP request method with comprehensive logging
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const startTime = Date.now();
    const method = options.method || 'GET';
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    };

    // Log request start
    logger.debug('API Request Started', {
      method,
      url,
      headers: this.sanitizeHeaders(config.headers as Record<string, string>),
      body: options.body ? this.sanitizeBody(options.body) : undefined
    });

    try {
      const response = await fetch(url, config);
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      // Log API call result
      logger.logAPICall(method, url, response.status, duration);

      if (!response.ok) {
        const error = new ApiError(
          data?.message || 'An error occurred',
          response.status,
          data?.errors || {}
        );

        logger.error('API Request Failed', {
          method,
          url,
          status: response.status,
          duration,
          errorMessage: error.message,
          errors: error.errors
        }, error);

        throw error;
      }

      logger.info('API Request Successful', {
        method,
        url,
        status: response.status,
        duration,
        responseSize: response.headers.get('content-length')
      });

      return data;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      const networkError = new ApiError(
        'Network error. Please check your connection and try again.',
        0,
        {}
      );

      logger.error('Network Error', {
        method,
        url,
        duration,
        errorMessage: (error as Error).message
      }, error as Error);

      throw networkError;
    }
  }

  /**
   * Sanitize headers for logging (remove sensitive data)
   */
  private sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
    const sanitized = { ...headers };
    if (sanitized['Authorization']) {
      sanitized['Authorization'] = '***REDACTED***';
    }
    if (sanitized['X-API-Key']) {
      sanitized['X-API-Key'] = '***REDACTED***';
    }
    return sanitized;
  }

  /**
   * Sanitize request body for logging (remove sensitive data)
   */
  private sanitizeBody(body: BodyInit): any {
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed.password) parsed.password = '***REDACTED***';
        if (parsed.token) parsed.token = '***REDACTED***';
        return parsed;
      } catch (e) {
        return 'Non-JSON body';
      }
    }
    return 'Binary or FormData body';
  }

  /**
   * Submit contact form
   */
  async submitContactForm(data: ContactFormData): Promise<ContactFormResponse> {
    return this.request<ContactFormResponse>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health', {
      method: 'GET',
    });
  }

  /**
   * Get available project types
   */
  getProjectTypes(): string[] {
    return [
      'Web Application',
      'Mobile App',
      'E-commerce',
      'API Development',
      'Database Design',
      'DevOps & Infrastructure',
      'Other'
    ];
  }

  /**
   * Get available budget ranges
   */
  getBudgetRanges(): string[] {
    return [
      'Under $5,000',
      '$5,000 - $10,000',
      '$10,000 - $25,000',
      '$25,000 - $50,000',
      '$50,000 - $100,000',
      'Over $100,000'
    ];
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  public status: number;
  public errors: Record<string, string[]>;

  constructor(message: string, status: number, errors: Record<string, string[]> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }

  /**
   * Get validation errors as a flat array
   */
  getValidationErrors(): ValidationError[] {
    const validationErrors: ValidationError[] = [];
    
    Object.entries(this.errors).forEach(([field, messages]) => {
      messages.forEach(message => {
        validationErrors.push({ field, message });
      });
    });
    
    return validationErrors;
  }

  /**
   * Check if error is a validation error (422)
   */
  isValidationError(): boolean {
    return this.status === 422;
  }

  /**
   * Check if error is a rate limit error (429)
   */
  isRateLimitError(): boolean {
    return this.status === 429;
  }

  /**
   * Check if error is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Check if error is a network error
   */
  isNetworkError(): boolean {
    return this.status === 0;
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;